import { isFunction, isObject } from './validators.js';
import { VIAFOURA_ROOT_UUID } from '../constants/parse.js';


export const UUID_PATTERN = '________-____-____-____-____________';
export const UUID_REGEX = new RegExp('^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$', 'i');
export const VF_UUID_PREFIX = '00000000-0000-4000-800';
export const VF_UUID_REGEX = new RegExp(`^${VF_UUID_PREFIX}[0-9a-f]-[0-9a-f]{12}$`, 'i');
export const UUID_LENGTH = 36;
export const HEX_RADIX = 16;
export const VFID_MAX = 4503599627370495;
export const PROTOCOL_PREFIX = new RegExp('^https?:\/\/', 'i');

/**
 *  isUuid - checks if the given string follows the general UUID pattern
 *
 *  @param {String} item - item to check whether it is a UUID or not
 *  @return {Boolean} - result of check
 */
export const isUuid = (item) => UUID_REGEX.test(item);

/**
 *  isVfUuid - checks if the given string follows the general VF UUID pattern
 *
 *  @param {String} item - item to check whether it is a VF UUID or not
 *  @return {Boolean} - result of check
 */
export const isVfUuid = (item) => VF_UUID_REGEX.test(item) && toVfId(item) <= VFID_MAX;

/**
 *  isVfId - checks if the given  item is a VfId or not
 *
 *  @param {Integer} item - item to check whether it is a VfId or not
 *  @return {Boolean} - result of check
 */
export const isVfId = (item) => Number.isInteger(item) && item >= 0 && item <= VFID_MAX;

/**
 *  toRelativeProtocol - replaces the protocol on a url with '//'
 *
 *  @param {String} url - a url string
 *  @return {String} - url mapped to its relative protocol equivalent
 */
export const toRelativeProtocol = (url) => url.replace(PROTOCOL_PREFIX, '//');

/**
 *  toUuid - converts a VfId to a UUID with safeguarding - identity if argument is already a UUID
 *
 *  @param {Integer} id - positive integer less than 14 characters (as hex) since JS only supports up to 53 bit ints
 *  @return {String} uuid - uuid conforming to the VfUuid standard
 *  @throws - if argument is not a valid VfId and not already a UUID
 */
export const toUuid = (id) => {
    if (UUID_REGEX.test(id)) {
        return id;
    }
    if (!Number.isInteger(id) || id < 0 || id > VFID_MAX) {
        throw new TypeError(`invalid argument: ${id}`);
    }

    const hexId = id.toString(HEX_RADIX);

    /* pad-left with 0s until total length is 13 - note that Array(1).join('0') === '' */
    const totalLengthBound = 14;
    const paddedHexId = Array(totalLengthBound - hexId.length).join('0') + hexId;
    return `${VF_UUID_PREFIX}${paddedHexId[0]}-${paddedHexId.substring(1)}`;
};

/**
 *  Convert a UUID to a long VfId
 *
 *  @param {String} uuid - any valid uuid string that retains isomorphic mapping with toUuid
 *  @return {Integer} id - id conforming to legacy VfId long integer id
 *  @throws - if argument is not a valid uuid
 */
export const toVfId = (uuid) => {
    // Add a special case for the viafoura root section
    if (uuid === VIAFOURA_ROOT_UUID) {
        return 0;
    }
    if (!VF_UUID_REGEX.test(uuid)) {
        throw new TypeError(`invalid argument: ${uuid}`);
    }
    /* recombine into hexadecimal string of VfId and then parse back into integer */
    const headIndex = 22;
    const tailIndex = 24;
    const combined = uuid[headIndex] + uuid.substring(tailIndex);
    return parseInt(combined, HEX_RADIX);
};

/**
 *  Convert a UUID to a long VfId
 *
 *  @param {Object} obj - object to be split
 *  @param {Object} groupPredicates - object containing keys and predicates where if a thruthy result
 *                                    leads to the pair into the specific inner object for that predicate key
 *  @return {Object} group map split up by keys determined by the group predicates passed in
 *  @throws - if group predicates is not an object or specific group predicate is not a function
 */
export const groupBy = (obj, groupPredicates) => {
    if (!isObject(groupPredicates)) {
        throw new Error('Group predicates passed in is not an object');
    }
    if (!Object.keys(groupPredicates).every((predicateKey) => isFunction(groupPredicates[predicateKey]))) {
        throw new Error('Cannot use a non-function as a predicate');
    }
    const groupMap = {};
    groupMap.default = {};
    Object.keys(groupPredicates).forEach((key) => {
        groupMap[key] = {};
    });

    /**
     * iterate over the keys of the object to split
     * iterate over the keys of the predicate object
     * if the predicate passes for the key/value from the object to split, add it to the appropriate group
     * otherwise add it to default
     */
    Object.keys(obj).forEach((key) => {
        const value = obj[key];
        let isAssigned = false;
        Object.keys(groupPredicates).forEach((predicateKey) => {
            const predicate = groupPredicates[predicateKey];
            if (predicate(key, value)) {
                groupMap[predicateKey][key] = value;
                isAssigned = true;
            }
        });
        if (!isAssigned) {
            groupMap.default[key] = value;
        }
    });

    return groupMap;
};

/**
 *  reduceMetaContent - helper/workhorse function for findMetaContent
 *                      allows reduction of a source array for the tag content
 *                      with the most precedence (name > property, order in titles)
 *                      or a defaultTo value if this is unavailable
 *
 *  @param {Array} source - array source of tag elements
 *  @param {Array} titles - ordered array of precedence to search the source for
 *  @param {String} defaultTo - default value if no results are found after reduction
 *  @return {String} - result or defaulted value
 */
const reduceMetaContent = (source, titles, defaultTo) => {
    const names = {};
    const properties = {};
    source.forEach((meta) => {
        /* grab the necessary attributes from the dom elements */
        const name = meta.getAttribute('name');
        const property = meta.getAttribute('property');
        const content = meta.getAttribute('content');
        if (!content) {
            return;
        }
        /**
         * write to the names/properties objects only the first time if found
         * this has the effect of preserving the precedence defined in the titles array
         */
        if (titles.includes(name) && !names[name]) {
            names[name] = content;
        }
        if (titles.includes(property) && !properties[property]) {
            properties[property] = content;
        }
    });
    /* find first truthy key (undefined if not found!) */
    const key = titles.find((title) => names[title] || properties[title]);
    return names[key] || properties[key] || defaultTo;
};

/**
 *  findMetaContent - finds all the meta tags in a source element and reduces these tags to
 *                    a single or defaulted content value based on the precedence set in the
 *                    titles array
 *
 *  @param {HTMLElement} element - DOM element eg. document
 *  @param {Array} titles - ordered array of precedence to search the source for
 *  @param {String} defaultTo - default value if no results are found after reduction
 *  @return {String} - result or defaulted value
 */
export const findMetaContent = (element, titles, defaultTo = '') =>
    reduceMetaContent(Array.from(element.querySelectorAll('meta')), titles, defaultTo);
